import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { audioSession } from '../audio/session';
import { t } from '../i18n/t';
import { useLiveSession } from '../session/live';
import { CHECK_INTERVAL_MS, noticeShown, pageBuild, servedBuild, shouldCheck } from './version';

/**
 * "A new version is ready · Reload", as a row of the shell above the nav. The
 * app never reloads itself: the learner chooses when, and is not asked during
 * a conversation or with a microphone open.
 */
export function UpdateNotice() {
  const { live } = useLiveSession();
  const recording = useSyncExternalStore(audioSession.subscribe, audioSession.isRecording);
  const [running] = useState(() => pageBuild(document));
  const [served, setServed] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastCheck = useRef(0);

  useEffect(() => {
    if (running === null) return;
    lastCheck.current = Date.now();
    // `no-store` on top of the server's `no-cache`: the answer is only worth
    // having if it is the server's now. A failure, offline included, is no
    // answer, never a notice.
    const check = () => {
      lastCheck.current = Date.now();
      fetch('/version.json', { cache: 'no-store' })
        .then((response) => (response.ok ? response.json() : null))
        .then((body: unknown) => {
          const build = servedBuild(body);
          if (build) setServed(build);
        })
        .catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      setDismissed(false);
      if (shouldCheck(document.visibilityState, lastCheck.current, Date.now())) check();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [running]);

  if (!noticeShown({ running, served, dismissed, busy: live || recording })) return null;

  return (
    <div role="status" className="border-base-300 bg-base-200 w-full border-t">
      <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-2">
        <RefreshCw aria-hidden className="h-5 w-5 shrink-0" />
        <p className="flex-1 text-sm">{t('update-available')}</p>
        <button type="button" className="btn btn-ghost btn-sm min-h-(--hit-min)" onClick={() => setDismissed(true)}>
          {t('update-later')}
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm min-h-(--hit-min)"
          onClick={() => window.location.reload()}
        >
          {t('update-reload')}
        </button>
      </div>
    </div>
  );
}
